import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { CheckCircle, Shield, Thermometer } from 'lucide-react';
import { Button } from './ui/button';

const GarageDoorOptions = () => {
  const rValues = [
    {
      title: "R-12 Doors",
      insulation: "1.0 inch insulation",
      description: "Great insulation for moderate climates",
      features: ["Energy efficient", "Noise reduction", "Durable construction"],
      bestFor: "Standard residential applications"
    },
    {
      title: "R-16 Doors",
      insulation: "1.5 inch insulation",
      description: "Superior insulation for harsh winters",
      features: ["Enhanced energy savings", "Better soundproofing", "Premium materials"],
      bestFor: "Saskatchewan weather conditions",
      popular: true
    },
    {
      title: "R-18 Doors",
      insulation: "1.8 inch insulation",
      description: "Maximum insulation & energy efficiency",
      features: ["Highest R-value available", "Extreme weather protection", "Top-tier quality"],
      bestFor: "Premium residential & commercial"
    }
  ];

  return (
    <section id="products" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Premium Garage Door Options
          </h2>
          <p className="text-xl text-gray-600">
            High-end quality doors with superior insulation for Saskatchewan's climate
          </p>
        </div>

        {/* R-Value Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
          {rValues.map((option, index) => (
            <Card 
              key={index}
              className={`border-2 hover:shadow-2xl transition-all duration-300 relative ${
                option.popular ? 'border-green-600 shadow-xl' : 'border-gray-200'
              }`}
            >
              {option.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                    MOST POPULAR
                  </div>
                </div>
              )}
              <CardHeader className="pt-8">
                <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl mb-4 mx-auto">
                  <Thermometer size={32} className="text-white" />
                </div>
                <CardTitle className="text-2xl text-center mb-2">
                  {option.title}
                </CardTitle>
                <div className="text-center mb-4">
                  <p className="text-lg font-bold text-blue-800">{option.insulation}</p>
                  <p className="text-gray-600 mt-2">{option.description}</p>
                </div>
                <CardDescription className="space-y-3">
                  {option.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                  <div className="pt-4 mt-4 border-t border-gray-200">
                    <p className="text-sm font-semibold text-gray-900">Best For:</p>
                    <p className="text-sm text-gray-600">{option.bestFor}</p>
                  </div>
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>

        {/* Styles & Colors Section */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Modern Styles & Colors
            </h3>
            <p className="text-lg text-gray-600">
              Choose from a wide variety of modern designs to match your home's aesthetic
            </p>
          </div>

          {/* Image Showcase */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-2xl mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_trusted-garage-pro/artifacts/uhd4twh4_WhatsApp%20Image%202026-06-02%20at%206.42.42%20PM.jpeg"
              alt="Modern Garage Door Styles & Colors"
              className="w-full rounded-2xl shadow-xl"
            />
          </div>

          {/* Style Categories */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-xl transition-all">
              <CardHeader>
                <CardTitle className="text-lg">Long Panel</CardTitle>
                <CardDescription>
                  Contemporary horizontal design in Walnut, Cedar, Charcoal, White, Black
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-xl transition-all">
              <CardHeader>
                <CardTitle className="text-lg">Short Panel</CardTitle>
                <CardDescription>
                  Classic sectional look in Walnut, Cedar, White, Charcoal, Black
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-xl transition-all">
              <CardHeader>
                <CardTitle className="text-lg">Flush</CardTitle>
                <CardDescription>
                  Sleek modern appearance in Walnut, Cedar, Charcoal, Black, White
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-xl transition-all">
              <CardHeader>
                <CardTitle className="text-lg">Carriage</CardTitle>
                <CardDescription>
                  Traditional elegant style in Walnut, Cedar, White, Charcoal, Black
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Quality Guarantee */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-3xl p-8 md:p-12 text-white text-center">
            <div className="flex items-center justify-center mb-6">
              <Shield size={48} />
            </div>
            <h3 className="text-3xl font-bold mb-4">
              High-End Quality Guaranteed
            </h3>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              All our garage doors feature premium construction, superior insulation (1" to 1.8"), 
              and are built to withstand Saskatchewan's harsh weather conditions. 
              Backed by manufacturer warranties and our satisfaction guarantee.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => {
                  const element = document.getElementById('quote');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white font-bold text-lg px-8 py-6"
              >
                Get Free Quote
              </Button>
              <Button 
                onClick={() => window.location.href = 'tel:306-262-5995'}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold text-lg px-8 py-6"
              >
                Call 306-262-5995
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GarageDoorOptions;